import React, { useState, useEffect } from "react";
import { Mail, MessageSquare, CreditCard, Check, ExternalLink, X, Edit3, Save, XCircle, BarChart3 } from "lucide-react";
import { disconnectTwilio } from "../../lib/twilio";

// Function to configure webhooks directly via Twilio API
async function configureWebhooksDirectly(accountSid: string, authToken: string, phoneNumber: string) {
  try {
    // Format phone number to E.164
    const formatToE164 = (phone: string) => {
      let cleaned = phone.replace(/\D/g, '');
      if (!cleaned.startsWith('1') && cleaned.length === 10) {
        cleaned = '1' + cleaned;
      }
      return cleaned.startsWith('+') ? cleaned : '+' + cleaned;
    };

    const formattedPhone = formatToE164(phoneNumber);
    
    // First, get the phone number SID
    const credentials = btoa(`${accountSid}:${authToken}`);
    const listResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers.json?PhoneNumber=${encodeURIComponent(formattedPhone)}`,
      {
        headers: {
          'Authorization': `Basic ${credentials}`
        }
      }
    );

    if (!listResponse.ok) {
      throw new Error('Failed to find phone number in Twilio account');
    }

    const listData = await listResponse.json();
    if (!listData.incoming_phone_numbers || listData.incoming_phone_numbers.length === 0) {
      throw new Error(`Phone number ${formattedPhone} not found in your Twilio account`);
    }

    const phoneNumberSid = listData.incoming_phone_numbers[0].sid;
    
    // Configure the webhooks
    const baseUrl = 'https://idvgoctfaarxiryxprpy.supabase.co/functions/v1';
    const voiceUrl = `${baseUrl}/twilio-call-webhook`;
    const statusCallbackUrl = `${baseUrl}/twilio-call-status`;

    const updateResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/IncomingPhoneNumbers/${phoneNumberSid}.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          VoiceUrl: voiceUrl,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST'
        })
      }
    );

    if (!updateResponse.ok) {
      const errorData = await updateResponse.json();
      throw new Error(errorData.message || 'Failed to update webhook URLs');
    }

    // Create TwiML Application for Voice SDK
    console.log('🔍 Creating TwiML Application for Voice SDK...');
    const twimlAppResponse = await fetch(
      `https://api.twilio.com/2010-04-01/Accounts/${accountSid}/Applications.json`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${credentials}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          FriendlyName: 'Voice SDK Application (Auto-created)',
          VoiceUrl: `${baseUrl}/twilio-voice-handler`,
          VoiceMethod: 'POST',
          StatusCallback: statusCallbackUrl,
          StatusCallbackMethod: 'POST'
        })
      }
    );

    let applicationSid = null;
    if (twimlAppResponse.ok) {
      const appData = await twimlAppResponse.json();
      applicationSid = appData.sid;
      console.log('✅ TwiML Application created:', applicationSid);
    } else {
      console.warn('⚠️ Failed to create TwiML Application, Voice SDK may not work');
    }

    return {
      success: true,
      voiceUrl,
      statusCallbackUrl,
      applicationSid
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to configure webhooks'
    };
  }
}
import StripeConnectButton from "../../components/StripeConnectButton";
import { useSupabase } from "../../contexts/SupabaseContext";
import { useBusiness } from "../../contexts/BusinessContext";
import { NexusLogo } from "../../components/ui/NexusLogo";
import { getSiteUrl } from "../../lib/siteConfig";
import AnalyticsSettings from "../../components/settings/AnalyticsSettings";

interface GmailConnection {
  id: string;
  email: string;
  connected_at: string;
  is_active: boolean;
}

export function Integrations() {
  const supabase = useSupabase();
  const { businessId } = useBusiness();
  const [showGmailSettings, setShowGmailSettings] = useState(false);
  const [showTwilioSettings, setShowTwilioSettings] = useState(false);
  const [showAnalyticsSettings, setShowAnalyticsSettings] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(false);
  const [twilioConnected, setTwilioConnected] = useState(false);
  const [analyticsConnected, setAnalyticsConnected] = useState(false);
  const [gmailConnections, setGmailConnections] = useState<GmailConnection[]>([]);
  const [isConnecting, setIsConnecting] = useState(false);
  const [twilioSettings, setTwilioSettings] = useState<any>(null);
  
  // Editing states for Twilio fields
  const [editingAccountSid, setEditingAccountSid] = useState(false);
  const [editingAuthToken, setEditingAuthToken] = useState(false);
  const [editingPhoneNumber, setEditingPhoneNumber] = useState(false);
  const [editingCallForwarding, setEditingCallForwarding] = useState(false);
  const [tempAccountSid, setTempAccountSid] = useState('');
  const [tempAuthToken, setTempAuthToken] = useState('');
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');
  const [tempCallForwardingEnabled, setTempCallForwardingEnabled] = useState(false);
  const [tempForwardingNumber, setTempForwardingNumber] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isConfiguringWebhooks, setIsConfiguringWebhooks] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Check for Stripe Connect return and update status
  useEffect(() => {
    const checkStripeConnectReturn = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('stripe_connect') === 'success' && businessId) {
        console.log('🔄 Stripe Connect return detected, checking account status...');
        
        try {
          const { data, error } = await supabase.functions.invoke('check-stripe-connect-status', {
            body: { businessId }
          });

          if (error) {
            console.error('❌ Failed to check Stripe status:', error);
          } else {
            console.log('✅ Stripe status updated:', data);
            // Clear the URL parameter to prevent repeated checks
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (error) {
          console.error('🚨 Error checking Stripe status:', error);
        }
      }
    };

    checkStripeConnectReturn();
  }, [businessId, supabase]);

  // Check Gmail connection status
  useEffect(() => {
    const checkGmailConnection = async () => {
      if (!businessId) return;
      
      try {
        const { data, error } = await supabase
          .from("gmail_connections")
          .select("*")
          .eq("business_id", businessId)
          .eq("is_active", true);

        if (error) throw error;
        setGmailConnections(data || []);
        setGmailConnected((data || []).length > 0);
      } catch (error) {
        console.error("Error checking Gmail connection:", error);
      }
    };

    checkGmailConnection();
  }, [businessId, supabase]);

  // Check Analytics connection status
  useEffect(() => {
    const checkAnalyticsConnection = async () => {
      if (!businessId) return;
      
      try {
        const { data, error } = await supabase
          .from("business_analytics_config")
          .select("is_active")
          .eq("business_id", businessId)
          .single();

        if (!error && data) {
          setAnalyticsConnected(data.is_active || false);
        }
      } catch (error) {
        console.error("Error checking Analytics connection:", error);
      }
    };

    checkAnalyticsConnection();
  }, [businessId, supabase]);

  // Check Twilio connection status
  useEffect(() => {
    const checkTwilioConnection = async () => {
      if (!businessId) return;
      
      try {
        const { data, error } = await supabase
          .from("businesses")
          .select("twilio_enabled, twilio_account_sid, twilio_auth_token, VITE_TWILIO_PHONE_NUMBER, twilio_call_forwarding_enabled, twilio_forwarding_number")
          .eq("id", businessId)
          .single();

        if (error) throw error;
        
        // Always set twilioSettings, even if null, to ensure fields are shown
        setTwilioSettings(data || {});
        
        // Consider Twilio connected if it's enabled and has basic credentials
        const isConnected = data?.twilio_enabled && 
                           data?.twilio_account_sid && 
                           data?.twilio_auth_token && 
                           data?.VITE_TWILIO_PHONE_NUMBER;
        
        setTwilioConnected(!!isConnected);
      } catch (error) {
        console.error("Error checking Twilio connection:", error);
        // Set empty object to still show fields even on error
        setTwilioSettings({});
      }
    };

    checkTwilioConnection();
  }, [businessId, supabase]);

  // Get the current business ID
  React.useEffect(() => {
    const getCurrentBusiness = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data: business } = await supabase
          .from("businesses")
          .select("id")
          .eq("owner_id", session.user.id)
          .single();

        if (business) {
          // This is a placeholder for the businessId update logic
          // The businessId is now managed by the useBusiness hook
        }
      }
    };

    getCurrentBusiness();
  }, [supabase]);

  const handleGmailClick = () => {
    setShowGmailSettings(!showGmailSettings);
    // Close Twilio if open
    if (showTwilioSettings) setShowTwilioSettings(false);
  };

  const handleTwilioClick = () => {
    setShowTwilioSettings(!showTwilioSettings);
    // Close Gmail if open
    if (showGmailSettings) setShowGmailSettings(false);
  };

  const handleAnalyticsClick = () => {
    setShowAnalyticsSettings(!showAnalyticsSettings);
    // Close other modals if open
    if (showGmailSettings) setShowGmailSettings(false);
    if (showTwilioSettings) setShowTwilioSettings(false);
  };

  const handleConnectGmail = async () => {
    setIsConnecting(true);
    
    try {
      // Use Vite environment variables
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUrl = `${supabaseUrl}/functions/v1/gmail-oauth-callback`;
      const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      
      console.log("Google Client ID:", clientId ? "Set" : "Not set");
      console.log("Supabase URL:", supabaseUrl);
      console.log("Redirect URL:", redirectUrl);
      
      if (!clientId) {
        alert("Google Client ID not configured. Please add VITE_GOOGLE_CLIENT_ID to your environment variables.");
        throw new Error("Google Client ID not configured");
      }

      if (!supabaseUrl) {
        alert("Supabase URL not configured. Please add VITE_SUPABASE_URL to your environment variables.");
        throw new Error("Supabase URL not configured");
      }

      const scope = encodeURIComponent([
        "https://www.googleapis.com/auth/gmail.readonly",
        "https://www.googleapis.com/auth/gmail.send",
        "https://www.googleapis.com/auth/gmail.modify",
        "https://www.googleapis.com/auth/userinfo.email",
        "https://www.googleapis.com/auth/userinfo.profile"
      ].join(" "));

      const state = btoa(JSON.stringify({ 
        businessId, 
        returnUrl: getSiteUrl() + window.location.pathname + window.location.search
      }));
      
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUrl)}&` +
        `response_type=code&` +
        `scope=${scope}&` +
        `access_type=offline&` +
        `prompt=consent&` +
        `state=${encodeURIComponent(state)}`;

      console.log("Redirecting to:", authUrl);
      
      window.location.href = authUrl;
      
    } catch (error) {
      console.error("Error initiating Gmail connection:", error);
      setIsConnecting(false);
    }
  };

  const handleDisconnectGmail = async (connectionId: string) => {
    try {
      const { error } = await supabase
        .from("gmail_connections")
        .update({ is_active: false })
        .eq("id", connectionId);

      if (error) throw error;
      
      // Reload connections
      setGmailConnections([]);
      setGmailConnected(false);
    } catch (error) {
      console.error("Error disconnecting Gmail:", error);
    }
  };

  const maskToken = (token: string) => {
    if (!token) return '';
    return token.length > 8 ? `${token.substring(0, 4)}...${token.substring(token.length - 4)}` : token;
  };

  const startEditing = (field: 'accountSid' | 'authToken' | 'phoneNumber' | 'callForwarding') => {
    switch (field) {
      case 'accountSid':
        setTempAccountSid(twilioSettings?.twilio_account_sid || '');
        setEditingAccountSid(true);
        break;
      case 'authToken':
        setTempAuthToken(twilioSettings?.twilio_auth_token || '');
        setEditingAuthToken(true);
        break;
      case 'phoneNumber':
        setTempPhoneNumber(twilioSettings?.VITE_TWILIO_PHONE_NUMBER || '');
        setEditingPhoneNumber(true);
        break;
      case 'callForwarding':
        setTempCallForwardingEnabled(twilioSettings?.twilio_call_forwarding_enabled || false);
        setTempForwardingNumber(twilioSettings?.twilio_forwarding_number || '');
        setEditingCallForwarding(true);
        break;
    }
  };

  const cancelEditing = (field: 'accountSid' | 'authToken' | 'phoneNumber' | 'callForwarding') => {
    switch (field) {
      case 'accountSid':
        setEditingAccountSid(false);
        setTempAccountSid('');
        break;
      case 'authToken':
        setEditingAuthToken(false);
        setTempAuthToken('');
        break;
      case 'phoneNumber':
        setEditingPhoneNumber(false);
        setTempPhoneNumber('');
        break;
      case 'callForwarding':
        setEditingCallForwarding(false);
        setTempCallForwardingEnabled(false);
        setTempForwardingNumber('');
        break;
    }
  };

  const saveTwilioField = async (field: 'accountSid' | 'authToken' | 'phoneNumber' | 'callForwarding') => {
    if (!businessId) return;
    
    setIsSaving(true);
    try {
      let updateData: any = {};
      
      switch (field) {
        case 'accountSid':
          updateData.twilio_account_sid = tempAccountSid;
          break;
        case 'authToken':
          updateData.twilio_auth_token = tempAuthToken;
          break;
        case 'phoneNumber':
          updateData.VITE_TWILIO_PHONE_NUMBER = tempPhoneNumber;
          break;
        case 'callForwarding':
          updateData.twilio_call_forwarding_enabled = tempCallForwardingEnabled;
          updateData.twilio_forwarding_number = tempForwardingNumber;
          break;
      }

      const { error } = await supabase
        .from("businesses")
        .update(updateData)
        .eq("id", businessId);

      if (error) throw error;

      // Update local state
      setTwilioSettings((prev: any) => ({ ...prev, ...updateData }));
      
      // Exit editing mode
      cancelEditing(field);
      
    } catch (error) {
      console.error("Error updating Twilio settings:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleActivateTwilio = async () => {
    if (!businessId || !twilioSettings) return;
    
    setIsSaving(true);
    try {
      // Update the business with enabled Twilio
      const { error } = await supabase
        .from("businesses")
        .update({ 
          twilio_enabled: true,
          twilio_status: 'active'
        })
        .eq("id", businessId);

      if (error) throw error;

      // Automatically configure webhooks using direct Twilio API
      setIsConfiguringWebhooks(true);
      let webhookConfigured = false;
      
      try {
        // Configure webhooks directly via Twilio REST API
        const webhookResult = await configureWebhooksDirectly(
          twilioSettings.twilio_account_sid,
          twilioSettings.twilio_auth_token,
          twilioSettings.VITE_TWILIO_PHONE_NUMBER
        );
        
        if (webhookResult.success) {
          console.log('✅ Webhooks configured successfully');
          webhookConfigured = true;
          
          // Update database to mark webhooks as configured and save Application SID
          const updateData: any = {
            twilio_webhooks_configured: true,
            twilio_webhook_configured_at: new Date().toISOString()
          };
          
          // Add Application SID if it was created successfully
          if (webhookResult.applicationSid) {
            updateData.twilio_application_sid = webhookResult.applicationSid;
            console.log('✅ TwiML Application SID saved to database:', webhookResult.applicationSid);
          }
          
          await supabase
            .from('businesses')
            .update(updateData)
            .eq('id', businessId);
        } else {
          console.error('❌ Webhook configuration failed:', webhookResult.error);
        }
      } catch (webhookError) {
        console.error('❌ Webhook configuration failed:', webhookError);
      }

      // Update local state
      setTwilioSettings((prev: any) => ({ 
        ...prev, 
        twilio_enabled: true,
        twilio_status: 'active',
        twilio_webhooks_configured: webhookConfigured
      }));
      
      setTwilioConnected(true);
      
    } catch (error) {
      console.error("Error activating Twilio integration:", error);
    } finally {
      setIsSaving(false);
      setIsConfiguringWebhooks(false);
    }
  };

  const handleDisconnectTwilio = async () => {
    if (!businessId) return;
    
    if (!confirm('Are you sure you want to disconnect Twilio? This will remove all settings and disable call forwarding.')) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const result = await disconnectTwilio({ businessId });

      if (result.success) {
        // Update local state
        setTwilioSettings(null);
        setTwilioConnected(false);
        
        // Reset editing states
        setEditingAccountSid(false);
        setEditingAuthToken(false);
        setEditingPhoneNumber(false);
        setEditingCallForwarding(false);
        setTempAccountSid('');
        setTempAuthToken('');
        setTempPhoneNumber('');
        setTempCallForwardingEnabled(false);
        setTempForwardingNumber('');
        
        console.log('✅ Twilio disconnected successfully');
      } else {
        console.error('❌ Failed to disconnect Twilio:', result.error);
      }
    } catch (error) {
      console.error("Error disconnecting Twilio:", error);
    } finally {
      setIsDisconnecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 relative z-0" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12 relative z-0">
        {/* Floating Container */}
        <div className="bg-gray-50 rounded-2xl shadow-sm border-0 p-8 md:p-16 mb-12 md:mb-20 relative z-0" style={{boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)'}}>
          {/* Header Section */}
          <header className="text-center mb-8 md:mb-16 relative z-0">
            <h1 className="mb-2 md:mb-4 tracking-tight relative z-0">
              <NexusLogo size="xl" /> <span className="text-xl md:text-3xl font-bold text-gray-800 ml-3 md:ml-4">Connect</span>
            </h1>
                          <p className="text-base md:text-lg text-gray-700 font-bold tracking-tight hidden md:block">Seamlessly integrate your favorite tools and apps</p>
          </header>
          
          {/* Integrations Grid */}
          <section className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-12">
          {/* Gmail Integration Card */}
          <div className="relative group">
            <div 
              onClick={handleGmailClick}
              className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-between cursor-pointer active:scale-95 active:bg-blue-50"
              style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
            >
                              <div className="text-center">
                  <div className={`w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full flex items-center justify-center ${
                    gmailConnected ? 'bg-red-50' : 'bg-gray-50'
                  }`}>
                    <Mail className={`w-4 h-4 md:w-8 md:h-8 ${gmailConnected ? 'text-red-600' : 'text-gray-400'}`} />
                  </div>
                  <h3 className="text-sm md:text-xl font-medium text-gray-900 mb-1 md:mb-3">Gmail</h3>
                  <p className="text-sm text-gray-500 mb-4 md:mb-6 hidden md:block">Email integration and automation</p>
                </div>
              
              <div className="text-center">
                {gmailConnected ? (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2"></div>
                      Connected
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-gray-50 text-gray-600 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1.5 md:mr-2"></div>
                      Connect
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Gmail Speech Bubble Dropdown */}
            {showGmailSettings && (
              <div className="absolute top-full mt-4 left-0 right-0 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 md:p-6 w-full md:w-[26rem] mx-1 md:mx-0 z-10">
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-white"></div>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-200"></div>
                
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900">Gmail Integration</h3>
                  <button
                    onClick={() => setShowGmailSettings(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>

                {gmailConnected && gmailConnections.length > 0 ? (
                  <div className="space-y-2 md:space-y-3">
                    {gmailConnections.map((connection) => (
                      <div key={connection.id} className="text-center">
                        <button
                          onClick={() => handleDisconnectGmail(connection.id)}
                          className="w-full inline-flex items-center justify-center px-3 py-2 md:px-6 md:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-xs md:text-sm font-medium"
                        >
                          Disconnect Gmail
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <button
                    onClick={handleConnectGmail}
                    disabled={isConnecting}
                    className="w-full inline-flex items-center justify-center px-3 py-2 md:px-6 md:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs md:text-sm font-medium"
                  >
                    {isConnecting ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 md:h-5 md:w-5 border-b-2 border-white mr-1 md:mr-2"></div>
                        <span className="text-xs md:text-sm">Connecting...</span>
                      </>
                    ) : (
                      <>
                        <ExternalLink className="h-3 w-3 md:h-5 md:w-5 mr-1 md:mr-2" />
                        <span className="text-xs md:text-sm">Connect Gmail</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Twilio Integration Card */}
          <div className="relative group">
            <div 
              onClick={handleTwilioClick}
              className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-between cursor-pointer active:scale-95 active:bg-blue-50"
              style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
            >
                              <div className="text-center">
                  <div className={`w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full flex items-center justify-center ${
                    twilioConnected ? 'bg-blue-50' : 'bg-gray-50'
                  }`}>
                    <MessageSquare className={`w-4 h-4 md:w-8 md:h-8 ${twilioConnected ? 'text-blue-600' : 'text-gray-400'}`} />
                  </div>
                  <h3 className="text-sm md:text-xl font-medium text-gray-900 mb-1 md:mb-3">Twilio</h3>
                  <p className="text-sm text-gray-500 mb-4 md:mb-6 hidden md:block">SMS and voice communications</p>
                </div>
              
              <div className="text-center">
                {twilioConnected ? (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2"></div>
                      Connected
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-gray-50 text-gray-600 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1.5 md:mr-2"></div>
                      Connect
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Twilio Speech Bubble Dropdown */}
            {showTwilioSettings && (
              <div className="absolute top-full mt-4 left-0 right-0 md:left-1/2 md:right-auto md:transform md:-translate-x-1/2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 md:p-6 w-full md:w-[28rem] mx-1 md:mx-0 z-10">
                <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-white"></div>
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-transparent border-b-gray-200"></div>
                
                <div className="flex items-center justify-between mb-3 md:mb-4">
                  <h3 className="text-sm md:text-lg font-semibold text-gray-900">Twilio Integration</h3>
                  <button
                    onClick={() => setShowTwilioSettings(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4 md:w-5 md:h-5" />
                  </button>
                </div>

                {twilioSettings ? (
      <div className="space-y-2 md:space-y-4">
                    {/* Account SID */}
                    <div className="p-2 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-1 md:mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-600">Account SID</span>
                        <div className="flex items-center space-x-1 md:space-x-2">
                          {!editingAccountSid && twilioSettings.twilio_account_sid && <Check className="h-3 w-3 md:h-4 md:w-4 text-green-500" />}
                          <button
                            onClick={() => editingAccountSid ? cancelEditing('accountSid') : startEditing('accountSid')}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSaving}
                          >
                            {editingAccountSid ? <XCircle className="w-3 h-3 md:w-4 md:h-4" /> : <Edit3 className="w-3 h-3 md:w-4 md:h-4" />}
                          </button>
                          {editingAccountSid && (
                            <button
                              onClick={() => saveTwilioField('accountSid')}
                              className="text-green-600 hover:text-green-700"
                              disabled={isSaving}
                            >
                              <Save className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {editingAccountSid ? (
                        <input
                          type="text"
                          value={tempAccountSid}
                          onChange={(e) => setTempAccountSid(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-xs md:text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 md:px-3 md:py-2"
                          placeholder="Enter Account SID"
                        />
                      ) : (
                        <span className={`text-xs md:text-sm font-mono ${twilioSettings.twilio_account_sid ? 'text-gray-900' : 'text-gray-400'}`}>
                          {twilioSettings.twilio_account_sid ? maskToken(twilioSettings.twilio_account_sid) : 'Not configured - click edit to add'}
                        </span>
                      )}
                    </div>

                    {/* Auth Token */}
                    <div className="p-2 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-1 md:mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-600">Auth Token</span>
                        <div className="flex items-center space-x-1 md:space-x-2">
                          {!editingAuthToken && twilioSettings.twilio_auth_token && <Check className="h-3 w-3 md:h-4 md:w-4 text-green-500" />}
                          <button
                            onClick={() => editingAuthToken ? cancelEditing('authToken') : startEditing('authToken')}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSaving}
                          >
                            {editingAuthToken ? <XCircle className="w-3 h-3 md:w-4 md:h-4" /> : <Edit3 className="w-3 h-3 md:w-4 md:h-4" />}
                          </button>
                          {editingAuthToken && (
                            <button
                              onClick={() => saveTwilioField('authToken')}
                              className="text-green-600 hover:text-green-700"
                              disabled={isSaving}
                            >
                              <Save className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {editingAuthToken ? (
                        <input
                          type="password"
                          value={tempAuthToken}
                          onChange={(e) => setTempAuthToken(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-xs md:text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 md:px-3 md:py-2"
                          placeholder="Enter Auth Token"
                        />
                      ) : (
                        <span className={`text-xs md:text-sm font-mono ${twilioSettings.twilio_auth_token ? 'text-gray-900' : 'text-gray-400'}`}>
                          {twilioSettings.twilio_auth_token ? maskToken(twilioSettings.twilio_auth_token) : 'Not configured - click edit to add'}
                        </span>
                      )}
                    </div>

                    {/* Phone Number */}
                    <div className="p-2 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-1 md:mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-600">Phone Number</span>
                        <div className="flex items-center space-x-1 md:space-x-2">
                          {!editingPhoneNumber && twilioSettings.VITE_TWILIO_PHONE_NUMBER && <Check className="h-3 w-3 md:h-4 md:w-4 text-green-500" />}
                          <button
                            onClick={() => editingPhoneNumber ? cancelEditing('phoneNumber') : startEditing('phoneNumber')}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSaving}
                          >
                            {editingPhoneNumber ? <XCircle className="w-3 h-3 md:w-4 md:h-4" /> : <Edit3 className="w-3 h-3 md:w-4 md:h-4" />}
                          </button>
                          {editingPhoneNumber && (
                            <button
                              onClick={() => saveTwilioField('phoneNumber')}
                              className="text-green-600 hover:text-green-700"
                              disabled={isSaving}
                            >
                              <Save className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {editingPhoneNumber ? (
                        <input
                          type="text"
                          value={tempPhoneNumber}
                          onChange={(e) => setTempPhoneNumber(e.target.value)}
                          onFocus={(e) => e.target.select()}
                          className="w-full text-xs md:text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 md:px-3 md:py-2"
                          placeholder="Enter Phone Number (+1234567890)"
                        />
                      ) : (
                        <span className={`text-xs md:text-sm font-mono ${twilioSettings.VITE_TWILIO_PHONE_NUMBER ? 'text-gray-900' : 'text-gray-400'}`}>
                          {twilioSettings.VITE_TWILIO_PHONE_NUMBER || 'Not configured - click edit to add'}
                        </span>
                      )}
                    </div>

                    {/* Call Forwarding */}
                    <div className="p-2 md:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center justify-between mb-1 md:mb-2">
                        <span className="text-xs md:text-sm font-medium text-gray-600">Call Forwarding</span>
                        <div className="flex items-center space-x-1 md:space-x-2">
                          {!editingCallForwarding && (twilioSettings.twilio_call_forwarding_enabled || twilioSettings.twilio_forwarding_number) && <Check className="h-3 w-3 md:h-4 md:w-4 text-green-500" />}
                          <button
                            onClick={() => editingCallForwarding ? cancelEditing('callForwarding') : startEditing('callForwarding')}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={isSaving}
                          >
                            {editingCallForwarding ? <XCircle className="w-3 h-3 md:w-4 md:h-4" /> : <Edit3 className="w-3 h-3 md:w-4 md:h-4" />}
                          </button>
                          {editingCallForwarding && (
                            <button
                              onClick={() => saveTwilioField('callForwarding')}
                              className="text-green-600 hover:text-green-700"
                              disabled={isSaving}
                            >
                              <Save className="w-3 h-3 md:w-4 md:h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      {editingCallForwarding ? (
                        <div className="space-y-2 md:space-y-3">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={tempCallForwardingEnabled}
                              onChange={(e) => setTempCallForwardingEnabled(e.target.checked)}
                              className="h-3 w-3 md:h-4 md:w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                            />
                            <span className="text-xs md:text-sm text-gray-700">Enable call forwarding</span>
                          </div>
                          {tempCallForwardingEnabled && (
                            <input
                              type="text"
                              value={tempForwardingNumber}
                              onChange={(e) => setTempForwardingNumber(e.target.value)}
                              onFocus={(e) => e.target.select()}
                              className="w-full text-xs md:text-sm font-mono text-gray-900 bg-white border border-gray-300 rounded px-2 py-1 md:px-3 md:py-2"
                              placeholder="Enter forwarding number (+1234567890)"
                            />
                          )}
                        </div>
                      ) : (
                        <div>
                          <span className={`text-xs md:text-sm font-medium ${twilioSettings.twilio_call_forwarding_enabled ? 'text-green-600' : 'text-gray-500'}`}>
                            {twilioSettings.twilio_call_forwarding_enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          {twilioSettings.twilio_call_forwarding_enabled && twilioSettings.twilio_forwarding_number && (
                            <div className="text-xs md:text-sm font-mono text-gray-900 mt-1">{twilioSettings.twilio_forwarding_number}</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Activate Integration Button */}
                    <div className="pt-2 md:pt-4 space-y-2">
                      <button
                        onClick={handleActivateTwilio}
                        disabled={isSaving || isConfiguringWebhooks || isDisconnecting || !twilioSettings.twilio_account_sid || !twilioSettings.twilio_auth_token || !twilioSettings.VITE_TWILIO_PHONE_NUMBER}
                        className="w-full inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs md:text-sm font-medium"
                      >
                        {isSaving || isConfiguringWebhooks ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-white mr-1 md:mr-2"></div>
                            <span className="text-xs md:text-sm">
                              {isConfiguringWebhooks ? 'Configuring Webhooks...' : 'Activating...'}
                            </span>
                          </>
                        ) : (
                          <>
                            <Check className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                            <span className="text-xs md:text-sm">
        {twilioSettings.twilio_webhooks_configured ? 'Reconfigure Integration' : 'Activate Integration'}
                            </span>
                          </>
                        )}
                      </button>
                      
                      {twilioSettings.twilio_enabled && (
                        <button
                          onClick={handleDisconnectTwilio}
                          disabled={isSaving || isConfiguringWebhooks || isDisconnecting}
                          className="w-full inline-flex items-center justify-center px-3 py-2 md:px-4 md:py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs md:text-sm font-medium"
                        >
                          {isDisconnecting ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-b-2 border-white mr-1 md:mr-2"></div>
                              <span className="text-xs md:text-sm">Disconnecting...</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="h-3 w-3 md:h-4 md:w-4 mr-1 md:mr-2" />
                              <span className="text-xs md:text-sm">🔌 Disconnect Twilio</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <p className="text-xs md:text-sm">Twilio not configured</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Analytics Integration Card */}
          <div className="relative group">
            <div 
              onClick={handleAnalyticsClick}
              className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-between cursor-pointer active:scale-95 active:bg-blue-50"
              style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
            >
              <div className="text-center">
                <div className={`w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full flex items-center justify-center ${
                  analyticsConnected ? 'bg-blue-50' : 'bg-gray-50'
                }`}>
                  <BarChart3 className={`w-4 h-4 md:w-8 md:h-8 ${analyticsConnected ? 'text-blue-600' : 'text-gray-400'}`} />
                </div>
                                 <h3 className="text-sm md:text-xl font-medium text-gray-900 mb-1 md:mb-3">Nexus Analytics</h3>
                <p className="text-sm text-gray-500 mb-4 md:mb-6 hidden md:block">Website traffic and user behavior</p>
              </div>
              
              <div className="text-center">
                {analyticsConnected ? (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-green-50 text-green-700 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full mr-1.5 md:mr-2"></div>
                      Connected
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 md:space-y-3">
                    <div className="inline-flex items-center bg-gray-50 text-gray-600 px-3 py-1.5 md:px-4 md:py-2 rounded-2xl text-xs md:text-sm font-medium">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1.5 md:mr-2"></div>
                      Connect
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stripe Integration Card */}
          <div className="relative group">
            <div 
              className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-between"
              style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
            >
              <div className="text-center">
                <div className="w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full bg-gray-50 flex items-center justify-center">
                  <CreditCard className="w-4 h-4 md:w-8 md:h-8 text-gray-400" />
                </div>
                <h3 className="text-sm md:text-xl font-medium text-gray-900 mb-1 md:mb-3">Stripe</h3>
                <p className="text-sm text-gray-500 mb-4 md:mb-6 hidden md:block">Payment processing and integrations</p>
              </div>
              
              <div className="text-center">
                <div className="space-y-2 md:space-y-3">
                  <StripeConnectButton size="medium" className="w-full" />
                </div>
              </div>
            </div>
          </div>

          {/* Coming Soon Cards */}
          <div 
            className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-center"
            style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
          >
            <div className="text-center">
              <div className="w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full bg-gray-50 flex items-center justify-center">
                <div className="w-1.5 h-1.5 md:w-3 md:h-3 bg-gray-300 rounded-full"></div>
              </div>
              <div className="space-y-1 md:space-y-3">
                <div className="inline-flex items-center bg-gray-50 text-gray-600 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1 md:mr-2"></div>
                  Coming Soon
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-center"
            style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
          >
            <div className="text-center">
              <div className="w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full bg-gray-50 flex items-center justify-center">
                <div className="w-1.5 h-1.5 md:w-3 md:h-3 bg-gray-300 rounded-full"></div>
              </div>
              <div className="space-y-1 md:space-y-3">
                <div className="inline-flex items-center bg-gray-50 text-gray-600 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1 md:mr-2"></div>
                  Coming Soon
                </div>
              </div>
            </div>
          </div>

          <div 
            className="bg-white rounded-2xl transition-all duration-200 p-4 md:p-8 border-0 aspect-square md:h-80 flex flex-col justify-center"
            style={{boxShadow: '0 2px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)'}}
          >
            <div className="text-center">
              <div className="w-8 h-8 md:w-16 md:h-16 mx-auto mb-2 md:mb-6 rounded-full bg-gray-50 flex items-center justify-center">
                <div className="w-1.5 h-1.5 md:w-3 md:h-3 bg-gray-300 rounded-full"></div>
              </div>
              <div className="space-y-1 md:space-y-3">
                <div className="inline-flex items-center bg-gray-50 text-gray-600 px-2 py-0.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-medium">
                  <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full mr-1 md:mr-2"></div>
                  Coming Soon
                </div>
              </div>
            </div>
          </div>
        </section>
        </div>
      </main>

      {/* Analytics Settings Modal */}
      <AnalyticsSettings 
        isOpen={showAnalyticsSettings} 
        onClose={() => {
          setShowAnalyticsSettings(false);
          // Refresh analytics connection status
          if (businessId) {
            supabase
              .from("business_analytics_config")
              .select("is_active")
              .eq("business_id", businessId)
              .single()
              .then(({ data }) => {
                if (data) {
                  setAnalyticsConnected(data.is_active || false);
                }
              });
          }
        }} 
      />
    </div>
  );
}
